import { TryFirstButtonUiTheme } from "@redotech/redo-model/team";
import { TnplContractTemplateInAutoCheck } from "@redotech/redo-model/tnpl-contract-template";
import cloneDeepWith from "lodash/cloneDeepWith";
import merge from "lodash/merge";

export interface TryFirstButtonUi {
  text: string;
  textColor: string;
  bgColor: string;
  borderColor: string;
  subtext: string;
  subtextColor: string;
  subtextFontSize: string;
}

// Empty defaults for input field display when values are not set
export const tryFirstButtonUiEmptyDefaults: TryFirstButtonUi = {
  text: "",
  textColor: "",
  bgColor: "",
  borderColor: "",
  subtext: "",
  subtextColor: "",
  subtextFontSize: "",
};

// Actual default values used if not set
export const defaultTryFirstButtonUi: TryFirstButtonUi = {
  text: "Try before you buy",
  textColor: "#424242",
  bgColor: "#FFFFFF",
  borderColor: "#EEEEEE",
  subtext: "Try it risk-free for %trialLength% days",
  subtextColor: "#424242",
  subtextFontSize: "12px",
};

// Shared helper function to replace contract template variables in strings
export const replaceContractVariables = (
  value: any,
  contractTemplate?: TnplContractTemplateInAutoCheck,
  useMinutesForTrialPeriod?: boolean,
): any => {
  if (typeof value === "string") {
    const trialLength = useMinutesForTrialPeriod
      ? contractTemplate?.trialPeriodMinutes?.toString()
      : contractTemplate?.trialPeriodDays?.toString();

    return value.replaceAll("%trialLength%", trialLength ?? "%trialLength%");
  }
  return value;
};

export const getEvaluatedTryFirstButtonSettings = ({
  settingsTemplate,
  contractTemplate,
  useMinutesForTrialPeriod,
}: {
  settingsTemplate?: Partial<TryFirstButtonUiTheme>;
  contractTemplate?: TnplContractTemplateInAutoCheck;
  useMinutesForTrialPeriod?: boolean;
} = {}): TryFirstButtonUi => {
  // Extract only the button UI properties, ignoring infoModal
  const buttonOnlySettings = {
    text: settingsTemplate?.text || undefined,
    textColor: settingsTemplate?.textColor || undefined,
    bgColor: settingsTemplate?.bgColor || undefined,
    borderColor: settingsTemplate?.borderColor || undefined,
    subtext: settingsTemplate?.subtext || undefined,
    subtextColor: settingsTemplate?.subtextColor || undefined,
    subtextFontSize: settingsTemplate?.subtextFontSize || undefined,
  };

  const settingsWithDefaults = merge(
    {},
    defaultTryFirstButtonUi,
    buttonOnlySettings,
  );

  // Apply contract template variable replacements
  const evaluatedSettings = cloneDeepWith(
    settingsWithDefaults,
    (value: any): any => {
      const replacedValue = replaceContractVariables(
        value,
        contractTemplate,
        useMinutesForTrialPeriod,
      );
      if (replacedValue !== value) {
        return replacedValue;
      }
      // Returning undefined will clone as normal
    },
  );

  return evaluatedSettings;
};
